import { type Control, Controller, type UseFormRegister } from 'react-hook-form'
import { Input } from 'app/components/ui/input'
import type { QuickCreateValues } from 'app/lib/contactQuickEntry'
import { LeadDatePicker } from './LeadDatePicker'
import { OptionPicker } from './OptionPicker'
import { OwnerPicker } from './OwnerPicker'
import { OptionRow } from './PickerButton'

const CHANNEL_OPTIONS = [`Digital Marketing`, `Traditional Marketing`, `Referral Marketing`].map((channel) => ({
  label: channel,
  value: channel,
}))

type Props = {
  control: Control<QuickCreateValues>
  register: UseFormRegister<QuickCreateValues>
  isSMC: boolean
  /** SMC owner picker searches users when the staff dashboard toggle is on, staff otherwise. */
  useUsersForOwner: boolean
}

/**
 * The "More options" section: everything with a default that the front desk
 * rarely changes at entry time. Same grammar as the row above the fold (label,
 * value, chevron); free text stays in inputs on their own row.
 */
export function QuickContactOptions({ control, register, isSMC, useUsersForOwner }: Props) {
  return (
    <div className="flex flex-col gap-3 border-t border-default pt-3">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <OptionRow label="Became a lead">
          <Controller
            name="becameLeadDate"
            control={control}
            render={({ field }) => <LeadDatePicker value={field.value} onChange={field.onChange} />}
          />
        </OptionRow>
        {isSMC && (
          <OptionRow label="Channel">
            <Controller
              name="channel"
              control={control}
              render={({ field }) => (
                <OptionPicker
                  label={CHANNEL_OPTIONS.find((o) => o.value === field.value)?.label ?? `Choose`}
                  options={CHANNEL_OPTIONS}
                  onChange={field.onChange}
                />
              )}
            />
          </OptionRow>
        )}
        {isSMC && (
          <OptionRow label="Owner">
            <Controller
              name="leadOwner"
              control={control}
              render={({ field }) => (
                <OwnerPicker value={field.value} onChange={field.onChange} useUsers={useUsersForOwner} />
              )}
            />
          </OptionRow>
        )}
      </div>
      {!isSMC && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Input {...register(`utmSource`)} placeholder="Source" aria-label="Source" autoComplete="off" />
          <Input {...register(`utmMedium`)} placeholder="Medium" aria-label="Medium" autoComplete="off" />
          <Input {...register(`utmCampaign`)} placeholder="Campaign" aria-label="Campaign" autoComplete="off" />
        </div>
      )}
    </div>
  )
}
